import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import type { DataLibraryWebLinkValues } from 'src/content/DataLibrary/schemas';
import { dataLibraryWebLinkSchema } from 'src/content/DataLibrary/schemas';
import { useUploadWidget } from 'src/context/UploadWidgetContext';
import useMutation from 'src/hooks/useMutation';
import {
  dataLibraryApi,
  useUploadDocumentsMutation
} from 'src/newServices/dataLibrary';
import type { RootState } from 'src/redux/store';
import { useSelector } from 'src/redux/store';
import { appendFormData } from 'src/services/utils';
import { ACCOUNT_TYPE, UPLOAD_TYPE } from 'src/types/enum';
import { ErrorContext } from 'src/utils/errorMappings';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { v4 as uuidV4 } from 'uuid';

export const useWebLinkManager = ({ onSuccess }: { onSuccess: () => void }) => {
  const { t } = useTranslation();
  const { parentId } = useUploadWidget();
  const user = useSelector((state: RootState) => state.auth.user);

  const dispatch = useDispatch();

  const [uploadDocuments, { isLoading: isUploading }] = useMutation({
    api: useUploadDocumentsMutation,
    errorContext: ErrorContext.FILES,
    subContext:
      (user?.accountType === ACCOUNT_TYPE.ORGANIZATION_ADMINISTRATOR ||
        user?.accountType === ACCOUNT_TYPE.ORGANIZATION_OWNER) &&
      ErrorContext.OA,
    fixedCacheKey: `uploadDocuments-${parentId}`
  });

  const methods = useForm<DataLibraryWebLinkValues>({
    resolver: zodResolver(dataLibraryWebLinkSchema(t)),
    defaultValues: { collectionName: '', links: [{ url: '' }] },
    mode: 'onChange'
  });
  const { control, reset, setValue, watch } = methods;

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: 'links'
  });

  const watchedLinks = watch('links');

  const handleInputChange = useCallback(
    (index: number, value: string) => {
      setValue(`links.${index}.url`, value, {
        shouldValidate: true,
        shouldDirty: true
      });

      const isLastField = index === fields.length - 1;
      const hasValue = value.trim() !== '';
      const canAddMore = fields.length < 5;

      if (isLastField && hasValue && canAddMore) {
        append({ url: '' }, { shouldFocus: false });
      }
    },
    [fields.length, append, setValue]
  );

  const handleFormSubmit = methods.handleSubmit(async (data) => {
    if (!parentId) {
      return;
    }

    const transactionId = uuidV4();
    const filteredLinks = data.links.filter((l) => l.url.trim() !== '');

    const formDataObj: any = {
      parent_id: parentId,
      upload_type: UPLOAD_TYPE.FOLDER,
      transaction_id: transactionId
    };

    const files: Array<{ filename: string; url: string }> = [];

    filteredLinks.forEach((linkObj) => {
      const fileUuid = uuidV4();
      formDataObj[`${fileUuid}_name`] = linkObj.url;
      formDataObj[`${fileUuid}_path`] = data.collectionName || '';
      files.push({ filename: `${fileUuid}`, url: linkObj.url });
    });

    formDataObj.files = JSON.stringify(files);
    const formData = appendFormData(formDataObj);

    await uploadDocuments({
      params: { params: { formData } },
      fallbackMsg: t(T.uploadDocFailed),
      successCallback: () => {
        dispatch(dataLibraryApi.util.invalidateTags(['Files']));
        onSuccess();
      }
    });
  });

  return {
    methods,
    fields,
    removeLink: remove,
    onSubmit: handleFormSubmit,
    resetForm: reset,
    handleInputChange: handleInputChange,
    isUploading,
    linkCount: watchedLinks?.length || 0
  };
};
